// src/anim/animMap.ts — Fighter → AnimDecision (ARCHITECTURE §5.5; ANIMATION §2 durum→klip tablosu ve §6 reaksiyonları koda döner).
// three İMPORT ETMEZ (Node'da test edilir). `decide(world, f, alpha, out)` her render karesinde çağrılır; `out` hem çıktı hem de ÖNCEKİ
// kararın belleğidir (base.clip / startTick / startAt / pauseAt / syncTime / attachTo / pitchDeg / pinHipsY okunur → iniş, çift zıplama,
// tutma sürekliliği, yeniden başlatma kararları).
// Sıcak yolda tahsis yok: tüm alanlar yerinde yazılır, dizgeler CLIPS sabitlerinden (string literal klip adı yasak); dilim sınırları ve
// klip süreleri KURULUMDA modül sabitlerine / Map önbelleklerine çekilir (parseClipName / clipDurationOf sıcak yolda ÇAĞRILMAZ).
// Zaman tabanı: startAt / pauseAt / endAt / syncTime KANONİK klip saniyesi (dilim eksen kaydırmaz — clipTable sözleşmesi).
// "yok" değerleri: clip '' (katman kapalı), pauseAt/endAt −1, syncTime −1 (pauseAt 0 sözleşmede hâlâ geçerli bir donma karesidir).
import type { AnimDecision, AnimPlay, Fighter, MoveDef, MoveStage, SimWorld } from '../core/types.ts';
import { FState, HitKind } from '../core/types.ts';
import { clamp, clamp01, hashNoise, lerp, radToDeg } from '../core/math.ts';
import { lenXZ, worldToLocalXZ } from '../core/vec3.ts';
import { NO_ID, NO_TICK, TICK_DT, TICK_HZ } from '../config/constants.ts';
import { T } from '../config/tuning.ts';
import { ALL_CLIP_NAMES, CLIPS, canonicalOf, clipDurationOf, parseClipName } from '../config/clipTable.ts';
import { CHARACTERS, IDLE_SET_DEFAULT, idleClipFor } from '../config/characters.ts';

/* ------------------------------------------------------------- yerel sabitler (tuning'de olmayanlar; needsWP0) */
/** A2 whiff Death_B@1.30-1.75 tS: ANIMATION 2.0; süre kuralı KNOCKDOWN.faceplantTicks (13 t) ile tutarlı olsun diye türetilir (2.08).
 *  Deviation: belge tS 2.0 ile 13 t uyuşmaz (0.45/2.0 = 13.5 t) → needsWP0 T.ANIM.faceplantRate. */
const FACEPLANT_RATE = (1.75 - 1.30) / (T.KNOCKDOWN.faceplantTicks * TICK_DT);
/** Carry'den düşürülme Lie_Down@1.0-1.4 tS 2.0 (ANIMATION §6.5; tuning'de yok). */
const LIE_DOWN_DROP_RATE = 2.0;
const LIE_DOWN_DROP_TICKS = Math.round((1.4 - 1.0) / LIE_DOWN_DROP_RATE * TICK_HZ);   // 12 t
/** Yerde rulo toplam açısı: fighterView.ts ROLL_TOTAL_DEG ile AYNI sayı — 3. turda tuning'e taşındı (T.ANIM.rollTotalDeg), iki modül
 *  de oradan okur (ANIMATION §6.2 "180°" ile deviation — gerekçe view'da: sırtüstü Lie_Idle'a dönüş için tek tur). */
const ROLL_TOTAL_DEG: number = T.ANIM.rollTotalDeg;
/** Kurtulma Dodge_Backward tS 1.2 (ANIMATION §6.3; DODGE.rate 1.25'ten farklı). */
const ESCAPE_DODGE_RATE = 1.2;
/** Çift zıplama sezimi: Fighter.doubleJumpLeft azalmış ∧ vy ≥ doubleV − pay (sim vy'yi 9'a ATAR; ilk zıplama v0 11 ile doubleJumpLeft tam).
 *  Kullanım sayısı sim/fighter.ts'de yerel (DOUBLE_JUMP_USES = 1) → needsWP0: config'e taşınsın; burada aynı değer yerel. */
const DOUBLE_JUMP_VY_MARGIN = 0.6;
const DOUBLE_JUMP_USES = 1;
/** Strafe (Running_Strafe_*) tS clamp'i ANIMATION'da verilmemiş → Running_A clamp'i kullanılır; Crawling alt sınırı Walking_A alt sınırı. */
const STRAFE_RATE = T.ANIM.runARate;
/** MoveDef'siz yedek hamle sayıları (ANIMATION §2.2: G2 Throw 0.20 tS 1.5; G1 Interact 0.345 tS 1.5; Hold Interact tS 1.5). */
const FALLBACK_THROW_START = 0.20, FALLBACK_THROW_RATE = 1.5;
const FALLBACK_GRAB_START = 0.345, FALLBACK_GRAB_RATE = 1.5;
const HOLD_RATE = 1.5;
/** Fade süreleri (ANIMATION §2.1 / §6 tabloları). tuning T.ANIM'de karşılığı YOK → needsWP0: fadeJumpIn/Out, fadeBlockLoko, fadeBlockEnter,
 *  fadeBlocking, fadeGuardBreakOut, fadePose, fadeTaunt, fadeMatchEnd eklenip buraya bağlansın. Tek yerde toplandı ki tuning'e taşıma mekanik olsun. */
const FADE = {
  blockLoko: 0.12,      // ANIMATION §2.1 blok yürüyüşü klipleri arası
  jumpIn: 0.05, jumpIdle: 0.12,                  // ANIMATION §2.1 Jump_Start girişi, Jump_Idle
  jumpLandIn: 0.06, jumpLandOut: 0.12,           // ANIMATION §2.1 Jump_Land
  dodgeIn: 0.05,                                 // ANIMATION §2.1 Dodge girişi
  blockEnterIn: 0.06,                            // ANIMATION §2.1 BlockEnter girişi
  blockingIn: 0.10,                              // ANIMATION §2.1 Blocking loop girişi
  parryTapIn: 0.03, parryTapOut: 0.08,           // ANIMATION §2.1 parry dokunuşu
  guardBreakOut: 0.15, parryStunOut: 0.12,       // ANIMATION §2.1 GuardBreak / ParryStun çıkışı
  pose: 0.05,                                    // Death_A_Pose / Lie_Idle / dilim pozları (Tumble, Knockdown, Lying, Roll)
  crawl: 0.15,                                   // ANIMATION §2.1 Crawling
  taunt: 0.15,                                   // ANIMATION §2.1 Taunt
  matchEnd: 0.2,                                 // ANIMATION §2.1 Win / Lose
  heldPoseIn: 0.15,                              // ANIMATION §6.3 Held → Death_A_Pose (13. t)
  heldHitIn: 0.05,                               // ANIMATION §6.3 Held Hit_A girişi
  carriedIn: 0.10,                               // ANIMATION §6.5 Carried Death_A_Pose
  carryUpperOut: 0.08,                           // ANIMATION §4 Carry üst katman çıkışı
  spawnIn: 0.05,                                 // ANIMATION §2.1 Spawn_Air girişi
} as const;
/** ÇIKIŞ fade'i YOK: geçişi gelen klip belirler (controller max(next.fade, prev.fadeOut); types.ts §5.2).
 *  Idle / loko / blok yürüyüşü için ANIMATION §2.1 zaten "0.20 / —" diyor: 0.20/0.15 yazılsaydı Idle → L1 Jab 0.20 s crossfade
 *  (impact 83 ms'de yumruk ağırlığı ≈ %42), Idle → Hit 0.20 (spec 0.03), Idle → BlockEnter 0.20 (spec 0.06) olurdu.
 *  AYNI mantık Jump_Start/Jump_Idle/BlockEnter/Blocking/Dodge/AirDodge/Escaping için de geçerli (tur-3 bulgusu): bu kliplerin
 *  belgedeki çıkış süreleri (0.10–0.15) hedefleri olan "→ Idle" geçişinde ZATEN Idle'ın 0.20 fade'i tarafından yeniliyordu, yani hiç
 *  görünmüyordu; buna karşılık küçük fade'li geçişleri şişiriyordu — Blocking → BlockHit/GuardBreak/Hit 0.03 → 0.12 (blok isabeti
 *  "lastik"), Jump_Idle → Jump_Land 0.06 → 0.12, Jump_Idle → Launch/Hit 0.03 → 0.12, Dodge → Hit (i-frame sonrası) 0.03 → 0.12 ve
 *  A1 Air Jab'da Jump_Idle tam gövde 0.12'de sönerken Punch_A#upper 0.05'te girdiğinden üst kemiklerde normalize karışım (impact
 *  83 ms'de yumruk ağırlığı ≈ 0.55; ANIMATION §4 "üst fadeIn 0.05" fiilen 0.12 oluyordu).
 *  Deviation: Blocking → Idle böylece 0.20 (spec 0.12), Jump_Land → Idle 0.20 (spec 0.12) okunur; 80 ms fark, sapma yönü belgeyle aynı
 *  ("gelen klip belirler"). Kalıcı çözüm needsWP0: controller kuralı "next.fade > 0 ? next.fade : prev.fadeOut" (types.ts §5.2 sözleşmesi). */
const NO_OUT = 0;

/* ------------------------------------------------------------- kurulum önbellekleri (sıcak yol yalnız Map.get / sabit) */
/** Dilim sınırları (kanonik sn) — kurulumda parse edilir. */
const sliceOf = (name: string): readonly [number, number] => parseClipName(name).slice!;
const BLOCK_ENTER_END = sliceOf(CLIPS.BLOCK_ENTER)[1];
/** Parry dokunuşu donma karesi (0.20): BLOCK_PARRY_TAP dilimi yerine AYNI BlockEnter action'ı 0.20'de durdurulur (ANIMATION §2.1 "geri sarmaz"). */
const PARRY_TAP_PAUSE = sliceOf(CLIPS.BLOCK_PARRY_TAP)[1];
const PARRY_OK_START = sliceOf(CLIPS.BLOCK_PARRY_OK)[0];
const PARRY_OK_END = sliceOf(CLIPS.BLOCK_PARRY_OK)[1];
const DEATH_B_FP_T0 = sliceOf(CLIPS.DEATH_B_FACEPLANT)[0], DEATH_B_FP_T1 = sliceOf(CLIPS.DEATH_B_FACEPLANT)[1];
const DEATH_A_KD_T0 = sliceOf(CLIPS.DEATH_A_KNOCKDOWN)[0], DEATH_A_KD_T1 = sliceOf(CLIPS.DEATH_A_KNOCKDOWN)[1];
const LIE_DOWN_DROP_T0 = sliceOf(CLIPS.LIE_DOWN_DROP)[0], LIE_DOWN_DROP_T1 = sliceOf(CLIPS.LIE_DOWN_DROP)[1];
const LIE_STANDUP_QUICK_T0 = sliceOf(CLIPS.LIE_STANDUP_QUICK)[0], LIE_STANDUP_QUICK_T1 = sliceOf(CLIPS.LIE_STANDUP_QUICK)[1];
const LIE_STANDUP_GETUP_T0 = sliceOf(CLIPS.LIE_STANDUP_GETUP)[0], LIE_STANDUP_GETUP_T1 = sliceOf(CLIPS.LIE_STANDUP_GETUP)[1];
const PICKUP_GRIP_T0 = sliceOf(CLIPS.PICKUP_GRIP)[0], PICKUP_GRIP_T1 = sliceOf(CLIPS.PICKUP_GRIP)[1];
const PICKUP_RAISE_T0 = sliceOf(CLIPS.PICKUP_RAISE)[0], PICKUP_RAISE_T1 = sliceOf(CLIPS.PICKUP_RAISE)[1];
const PICKUP_ITEM_T1 = sliceOf(CLIPS.PICKUP_ITEM)[1];
const INTERACT_LIFT_T0 = sliceOf(CLIPS.INTERACT_LIFT)[0], INTERACT_LIFT_T1 = sliceOf(CLIPS.INTERACT_LIFT)[1];
/** Klip süreleri (dilimde t1) — kurulumda. */
const HIT_A_DUR = clipDurationOf(CLIPS.HIT_A);
const HIT_B_DUR = clipDurationOf(CLIPS.HIT_B);
const HIT_B_PARRY_END = HIT_B_DUR;   // ANIMATION §2.1 ParryStun Hit_B [0–0.87] = klip sonu (0.867)
const BLOCK_HIT_DUR = clipDurationOf(CLIPS.BLOCK_HIT);
const BLOCK_HIT_SHORT_DUR = clipDurationOf(CLIPS.BLOCK_HIT_SHORT);
const JUMP_LAND_SHORT_DUR = clipDurationOf(CLIPS.JUMP_LAND_SHORT);
const JUMP_START_DOUBLE_DUR = clipDurationOf(CLIPS.JUMP_START_DOUBLE);
const SPAWN_AIR_DUR = clipDurationOf(CLIPS.SPAWN_AIR);
const THROW_DUR = clipDurationOf(CLIPS.THROW);
const INTERACT_DUR = clipDurationOf(CLIPS.INTERACT);
/** Ad → süre / katman / kanonik önbellekleri: ALL_CLIP_NAMES üzerinden kurulumda; hamle yolu (MoveDef/MoveStage klipleri), Dodge, Taunt,
 *  Idle_B varyantı sıcak yolda yalnız Map.get. Bilinmeyen ad (olmamalı) eski yola düşer. needsWP0: clipTable'a taşınabilir. */
const CLIP_DUR = new Map<string, number>();
const CLIP_LAYER = new Map<string, 'upper' | 'lower' | null>();
const CLIP_CANON = new Map<string, string>();
for (const name of ALL_CLIP_NAMES) {
  const p = parseClipName(name);
  CLIP_DUR.set(name, clipDurationOf(name)); CLIP_LAYER.set(name, p.layer); CLIP_CANON.set(name, p.base);
}
const durOf = (name: string): number => { const d = CLIP_DUR.get(name); return d !== undefined ? d : clipDurationOf(name); };
const layerOf = (name: string): 'upper' | 'lower' | null => { const l = CLIP_LAYER.get(name); return l !== undefined ? l : parseClipName(name).layer; };
const canonOf = (name: string): string => { const c = CLIP_CANON.get(name); return c !== undefined ? c : canonicalOf(name); };

/** Havada olduğu anlaşılan önceki base klipler (inişte Jump_Land kararı). Dodge_* yer dodge'unda / kurtulmada da kullanılır → iniş kararı
 *  ayrıca önceki kararın pinHipsY'sine bakar (hava klipleri Y-bastır, yer dodge'u değil). Launch (Death_A@0-0.267) yerde biterse Jump_Land. */
const AIR_BASES: ReadonlySet<string> = new Set([CLIPS.JUMP_START, CLIPS.JUMP_START_DOUBLE, CLIPS.JUMP_IDLE, CLIPS.JUMP_IDLE_LOWER,
  CLIPS.KICK_FLY, CLIPS.DODGE_B, CLIPS.DODGE_F, CLIPS.DODGE_L, CLIPS.DODGE_R, CLIPS.DEATH_A_LAUNCH]);

/* ------------------------------------------------------------- yardımcılar */

export const emptyPlay = (): AnimPlay => ({ clip: '', loop: false, rate: 1, fade: 0, fadeOut: 0, startAt: 0, pauseAt: -1, endAt: -1, startTick: 0 });
/** Nötr karar (kurulumda bir kez; sonra `decide` yerinde yazar). */
export function createDecision(): AnimDecision {
  return {
    base: emptyPlay(), upper: emptyPlay(), syncTime: -1, paused: false, additiveWeight: 0,
    pitchDeg: 0, rollDeg: 0, tumbleDeg: 0, squashX: 1, squashY: 1, pinHipsY: false,
    attachTo: NO_ID, attachFwd: 0, attachUp: 0, attachSide: 0, attachYawDeg: 0, visible: true, opacity: 1, shrink: 0,
  };
}

function setPlay(p: AnimPlay, clip: string, loop: boolean, rate: number, fade: number, fadeOut: number, startAt: number, pauseAt: number, endAt: number, startTick: number): AnimPlay {
  p.clip = clip; p.loop = loop; p.rate = rate; p.fade = fade; p.fadeOut = fadeOut;
  p.startAt = startAt; p.pauseAt = pauseAt; p.endAt = endAt; p.startTick = startTick;
  return p;
}
const closePlay = (p: AnimPlay): void => { p.clip = ''; };

const _local = { x: 0, y: 0, z: 0 };

/* FastFall pitch rampası TİCK tabanlıdır (ANIMATION §2.1 "4 t'de +10°"): ilk hâl her RENDER karesinde +10°/4 ekliyordu (rampa
 * 120 Hz'de 33 ms, 30 Hz'de 133 ms), ikinci hâl dövüşçü başına modül belleği tutuyordu (dünyalar arası paylaşımlı, "boşluk"
 * sezimiyle). 3. turda sim alanı geldi: Fighter.fastFallTick (fighter.ts hızlı iniş kenarında yazar, zıplama/iniş NO_TICK'e
 * çeker) → rampa doğrudan ondan; bellek de sezim de kalktı (entegrasyon eki). */
/**
 * Durum yaşından düşülecek DONMUŞ tick sayısı (ANIMATION §9 madde 19: "hitstop … sonrasında zaman sıçraması yok").
 * Sim donarken (kendi hitstop'u ya da ring-out globalHitstop'u) görsel `d.paused` ile durur, ama `tick − stateTick` akmaya devam
 * eder → düzeltme olmadan çözülme anında syncTime SYNC_EPS'i (0.05) aşarak sıçrardı: Hit_A tS 1.8 × 7 t = 0.21 s (baş geri tepe
 * karesi atlanır), Knockdown Death_A@0.37-0.55 (0.18 s) 7 t sonrası 0.117 s ileri (çakılmanın ~%65'i kaybolur), BlockHit'te 2 t.
 * 3. turda sim alanı geldi (entegrasyon eki): Fighter.frozenTicks — stepTimers kendi hitstop'unda, world.step globalHitstop'ta
 * artırır, setState sıfırlar. Önceki türetme (stateUntil − stateTick − nominal − hitstop) yalnız SÜREYİ UZATAN donmaları
 * görebiliyordu; ring-out donması süreyi uzatmadığı için herkeste 7 t'lik sıçrama kalıyordu — alan onu da kapatır.
 * Yaş `st` (Hitstun/Launched'da lastHitTick) stateTick'ten sonraysa sayaç fazla düşebilir; Math.max(0, …) ile taban 0'da tutulur.
 */
const frozenTicks = (f: Fighter): number => f.frozenTicks;

/** Loko tS = hız / doğal hız, clamp. */
const lokoRate = (v: number, natural: number, r: readonly [number, number]): number => clamp(v / natural, r[0], r[1]);

/** Dodge klibi: sim `dodgeClip` (ClipDataSet indeksi) → ad; yoksa dodge yönünün karaktere göre en yakın ekseni. */
function dodgeClipOf(world: SimWorld, f: Fighter): string {
  if (f.dodgeClip >= 0) {
    const name = world.clips.byIndex(f.dodgeClip).name;
    if (name === CLIPS.DODGE_B || name === CLIPS.DODGE_F || name === CLIPS.DODGE_L || name === CLIPS.DODGE_R) return name;
  }
  worldToLocalXZ(_local, f.yaw, f.dodgeDirX, f.dodgeDirZ);
  if (Math.abs(_local.x) >= Math.abs(_local.z)) return _local.x >= 0 ? CLIPS.DODGE_F : CLIPS.DODGE_B;
  return _local.z > 0 ? CLIPS.DODGE_L : CLIPS.DODGE_R;
}

/** Hamle başlangıç tick'i: aynı klip ilerleyen senkronla devam ediyorsa önceki startTick korunur (hitstop'ta moveTick durur, tick akar →
 *  tick − moveTick kayardı); klip değişti ya da senkron geri gitti (yeni hamle örneği: art arda iki L1) → yeni startTick = tick. */
function moveStartTick(prevClip: string, prevStartTick: number, prevStartAt: number, prevSync: number, clip: string, startAt: number, sync: number, loop: boolean, tick: number): number {
  if (prevClip === clip && prevStartAt === startAt && prevSync >= 0 && (loop || sync >= prevSync - 1e-6)) return prevStartTick;
  return tick;   // klip / startAt değişti (Dive Chop inişi: 0.55 → landSyncTime 0.72) ya da senkron geri gitti → reset + time yazımı
}

/** holdBranch hedefi id → indeks (H1 → Sweep aşama 1 geçişinin fade'i için). world.moves dizisi başına BİR KEZ kurulur; sıcak yolda
 *  yalnız Map.get (tahsis yok). needsWP0: MoveDef.holdBranchIdx çözümlenmiş indeks olsaydı bu önbellek gerekmezdi. */
let branchMoves: readonly MoveDef[] | null = null;
const branchIdx = new Map<string, number>();
function moveIndexIn(moves: readonly MoveDef[], id: string): number {
  if (branchMoves !== moves) { branchIdx.clear(); for (let i = 0; i < moves.length; i++) branchIdx.set(moves[i]!.id, i); branchMoves = moves; }
  const i = branchIdx.get(id);
  return i !== undefined ? i : -1;
}

/** Hamle aşaması seçimi (DESIGN §4 stages): moveTick'e göre; aşama ofseti önceki aşamaların ticks toplamı (holdBranch: moveTick 15 korunur,
 *  stages[0].ticks 15 → aşama 1 elapsed 0). Sim stageIdx'i verir; aşama süresi dolmuşsa (sim henüz ilerletmediyse) ileri alınır.
 *  Sıcak yol: modül scratch'ine yazar, tahsis yok. */
const _stage = { stage: null as MoveStage | null, elapsed: 0, idx: 0 };
function stageInto(move: MoveDef, f: Fighter): typeof _stage {
  const st = move.stages;
  if (!st || st.length === 0) { _stage.stage = null; _stage.elapsed = f.moveTick; _stage.idx = 0; return _stage; }
  let idx = clamp(f.stageIdx, 0, st.length - 1);
  let offset = 0;
  for (let i = 0; i < idx; i++) offset += Math.max(0, st[i]!.ticks);
  while (idx < st.length - 1 && st[idx]!.ticks >= 0 && f.moveTick - offset >= st[idx]!.ticks) { offset += st[idx]!.ticks; idx++; }
  _stage.stage = st[idx]!; _stage.elapsed = Math.max(0, f.moveTick - offset); _stage.idx = idx;
  return _stage;
}

/* ------------------------------------------------------------- hamle (Attack / Throwing / GrabAttempt / PickingUp) */

/** MoveDef'ten base/upper/syncTime; true döner. Katman: upperOnly → upper = klip, base = Jump_Idle#lower (havada) / loko (yerde). */
function applyMove(world: SimWorld, f: Fighter, move: MoveDef, out: AnimDecision, tick: number, prevSync: number, prevUpper: string, prevUpperStart: number, prevUpperStartAt: number): void {
  const s = stageInto(move, f);
  const stage = s.stage;
  const clip = stage ? stage.clip : move.clip;
  let startAt = stage ? stage.clipStart : move.clipStart;
  const rate = stage ? stage.clipRate : move.clipRate;
  const loop = stage?.loop === true;
  let pauseAt = stage ? (stage.pauseAt ?? -1) : (move.pauseAt ?? -1);
  const dur = durOf(clip);
  const endAt = stage ? (stage.clipEnd ?? dur) : (move.clipEnd ?? dur);
  let elapsed = s.elapsed;
  // Dive Chop (landPhase 'windup'): havada windup uzar → klip landSyncTime'da bekler; inişte klip zamanı landSyncTime'a senkronlanır,
  // aşama başlangıcı = iniş tick'i (WP3 f.stageStartTick'e iniş tick'ini yazar; types.ts landSyncTime notu)
  if (!stage && move.landPhase === 'windup' && move.landSyncTime !== undefined) {
    if (!f.grounded) { pauseAt = move.landSyncTime; }
    else {
      startAt = move.landSyncTime;
      elapsed = f.stageStartTick !== NO_TICK && f.stageStartTick >= 0 && f.stageStartTick <= tick ? tick - f.stageStartTick : f.moveTick;
    }
  }
  let sync = startAt + elapsed * TICK_DT * rate;
  if (loop) sync = dur > 0 ? sync % dur : 0;
  else { if (pauseAt >= 0 && sync > pauseAt) sync = pauseAt; if (sync > endAt) sync = endAt; }

  // giriş fade'i: aşama > 0 → MoveStage.fade; kombo içi 0.04; bloktan saldırı 0.04; aksi 0.05
  const prevBase = out.base.clip;
  const fromBlock = prevBase === CLIPS.BLOCKING || prevBase === CLIPS.BLOCK_ENTER;
  const fade = stage && s.idx > 0 ? stage.fade : (f.comboStep > 0 || fromBlock ? T.ANIM.fadeComboIn : T.ANIM.fadeAttackIn);
  // çıkış fade'i: controller crossfade = max(next.fade, prev.fadeOut) (types.ts AnimPlay) → aşama geçişi KENDİ fade'ini (0.04/0.06,
  // ANIMATION §4 / §9 madde 23) alsın diye sonraki aşama varsa onun giriş fade'i; holdBranch hedefi (H1 → Sweep aşama 1, H1_kick_run → Leap)
  // için hedefin aşama 1 fade'i; son aşama / düz hamle saldırıdan çıkış 0.10.
  let fadeOut = T.ANIM.fadeAttackOut;
  const stages = move.stages;
  if (stages && s.idx < stages.length - 1) fadeOut = stages[s.idx + 1]!.fade;
  else if (move.holdBranch !== undefined) {
    const bi = moveIndexIn(world.moves, move.holdBranch);
    const bs = bi >= 0 ? world.moves[bi]!.stages : undefined;
    if (bs && bs.length > 1) fadeOut = bs[1]!.fade;
  }

  const upperOnly = move.upperOnly === true && layerOf(clip) === 'upper';
  if (upperOnly) {
    if (move.landPhase === 'recover' && f.grounded) {
      // İniş (sim/moves.ts: grounded → stageIdx 1, stageStartTick = iniş tick'i, JUMP.landTicks 8 t kilit): üst katman kapanır (çıkış
      // fade'i jumpLandIn 0.06 — ANIMATION §4 "iniş Jump_Land tam gövdeye 0.06"), tam gövde Jump_Land (§2.1 JumpLand: toz + squash + land).
      // Hamle bitip Idle'a geçince oradaki iniş sezimi (prevBase JUMP_LAND_SHORT + startTick belleği) aynı oynatmayı 8 t'ye tamamlar.
      const landStart = f.stageIdx >= 1 && f.stageStartTick !== NO_TICK && f.stageStartTick >= 0 && f.stageStartTick <= tick ? f.stageStartTick
        : prevBase === CLIPS.JUMP_LAND_SHORT ? out.base.startTick : tick;
      closePlay(out.upper);
      setPlay(out.base, CLIPS.JUMP_LAND_SHORT, false, T.ANIM.jumpLandRate, FADE.jumpLandIn, FADE.jumpLandOut, 0, -1, -1, landStart);
      out.syncTime = Math.min(JUMP_LAND_SHORT_DUR, (tick - landStart) * TICK_DT * T.ANIM.jumpLandRate);
      out.pinHipsY = true;
      out.pitchDeg = 0;
      return;
    }
    const startTick = moveStartTick(prevUpper, prevUpperStart, prevUpperStartAt, prevSync, clip, startAt, sync, loop, tick);
    // üst katman çıkışı: iniş hamlesinde Jump_Land'e 0.06 (§4); aksi saldırıdan çıkış
    const upperOut = move.landPhase === 'recover' ? FADE.jumpLandIn : fadeOut;
    setPlay(out.upper, clip, loop, rate, prevUpper === '' ? T.ANIM.fadeUpperIn : fade, upperOut, startAt, pauseAt, endAt, startTick);
    // alt gövde: havada Jump_Idle#lower; yerde (landPhase'siz üst katman hamlesi) loko alt katmanı
    lowerLoko(f, out.base, f.grounded);
    out.pinHipsY = !f.grounded;
  } else {
    const startTick = moveStartTick(out.base.clip, out.base.startTick, out.base.startAt, prevSync, clip, startAt, sync, loop, tick);
    setPlay(out.base, clip, loop, rate, fade, fadeOut, startAt, pauseAt, endAt, startTick);
    closePlay(out.upper);
    // Y rejimi: yerde Y klipte (bastır yalnız XZ; Leap Smash D2'nin hop'u klipte kalır); havada tam gövde (Flying Kick) Y-bastır.
    // Jump_Chop istisnası yalnız YERDEKİ Leap Smash içindir: Dive Chop (landPhase 'windup', havada 0.72'de bekler, klip hips 0.30–0.80)
    // ANIMATION §2.3 "Y-bastır" → havada pin; inişte klipte (0.406 → 0.30 düşüşü çarpma tick'ine denk gelir, şok halkası maskeler —
    // pin tutulsaydı 20 t'lik çömelme pozunda ayaklar 0.1 u yüzerdi; ölçüm: hips 0.72→1.33 boyunca 0.26–0.30).
    out.pinHipsY = !f.grounded && (canonOf(clip) !== CLIPS.JUMP_CHOP || move.landPhase === 'windup');
  }
  out.syncTime = sync;
  out.pitchDeg = move.rootPitch ?? 0;
}

/** Alt gövde loko (Carry ve hava hamleleri): havada Jump_Idle#lower; yerde hıza göre Idle_A / Walking_A / Running_A #lower.
 *  fadeOut 0 (NO_OUT): ANIMATION §2.1 Idle/Walk/Run çıkış fade'i "—" — geçiş süresini GELEN klip belirler (controller max(next.fade,
 *  prev.fadeOut)); 0.15 yazılsaydı Jump_Idle#lower → Jump_Land 0.06 (§4) ve Idle_A#lower → Throw/Drop 0.08 geçişleri 0.15'e şişerdi. */
function lowerLoko(f: Fighter, p: AnimPlay, grounded: boolean): void {
  if (!grounded) { setPlay(p, CLIPS.JUMP_IDLE_LOWER, true, 1, T.ANIM.fadeUpperIn, NO_OUT, 0, -1, -1, 0); return; }
  const v = lenXZ(f.vel);
  if (v < T.ANIM.walkMin) setPlay(p, CLIPS.IDLE_A_LOWER, true, 1, T.ANIM.fadeIdle, NO_OUT, 0, -1, -1, 0);
  else if (v < T.ANIM.runAMin) setPlay(p, CLIPS.WALKING_A_LOWER, true, lokoRate(v, T.ANIM.naturalWalkA, T.ANIM.walkRate), T.ANIM.fadeLoko, NO_OUT, 0, -1, -1, 0);
  else setPlay(p, CLIPS.RUNNING_A_LOWER, true, lokoRate(v, T.ANIM.naturalRunA, T.ANIM.runARate), T.ANIM.fadeLoko, NO_OUT, 0, -1, -1, 0);
}

/* ------------------------------------------------------------- loko / idle */

function fullLoko(world: SimWorld, f: Fighter, out: AnimDecision, tick: number): void {
  const v = lenXZ(f.vel);
  const p = out.base;
  if (v < T.ANIM.walkMin) {
    const def = CHARACTERS[f.charIdx];
    const idleSet = def ? def.idle : IDLE_SET_DEFAULT;
    const idle = idleClipFor(idleSet, f.weapon, f.hasShield);
    // Idle_A seti: 30 s'de bir Idle_B tek tur (durum yaşına göre; bot senkronunu id'li ofset kırar)
    if (idle === idleSet.oneHand && idleSet.variant !== '') {
      const period = Math.round(T.ANIM.idleBEverySec * TICK_HZ);
      const age = tick - f.stateTick;
      const phase = age % period;
      const varTicks = Math.round(durOf(idleSet.variant) * TICK_HZ);
      if (age >= period && phase < varTicks) {
        setPlay(p, idleSet.variant, false, 1, T.ANIM.fadeIdle, NO_OUT, 0, -1, -1, tick - phase);
        out.syncTime = -1; return;
      }
    }
    const offset = hashNoise(world.seed ^ f.seed, 0, f.id) * T.ANIM.idleOffsetMax;   // rastgele ofset 0–1.0 s (deterministik)
    setPlay(p, idle, true, 1, T.ANIM.fadeIdle, NO_OUT, offset, -1, -1, 0);
  } else if (v < T.ANIM.runAMin) {
    setPlay(p, CLIPS.WALKING_A, true, lokoRate(v, T.ANIM.naturalWalkA, T.ANIM.walkRate), T.ANIM.fadeLoko, NO_OUT, 0, -1, -1, 0);
  } else if (v < T.ANIM.runBMin) {
    setPlay(p, CLIPS.RUNNING_A, true, lokoRate(v, T.ANIM.naturalRunA, T.ANIM.runARate), T.ANIM.fadeLoko, NO_OUT, 0, -1, -1, 0);
  } else {
    setPlay(p, CLIPS.RUNNING_B, true, lokoRate(v, T.ANIM.naturalRunB, T.ANIM.runBRate), T.ANIM.fadeLoko, NO_OUT, 0, -1, -1, 0);
  }
}

/** Blok + hareket (tam gövde; ANIMATION §2.1): yöne göre Walking_A / Walking_Backwards / Running_Strafe_L/R, tS = v / doğal.
 *  Giriş 0.12 (klipler arası); çıkış 0 (NO_OUT) — Blocking 0.10 / bloktan saldırı 0.04 kendi giriş fade'iyle. */
function blockLoko(f: Fighter, p: AnimPlay, v: number): void {
  worldToLocalXZ(_local, f.yaw, f.vel.x, f.vel.z);
  if (Math.abs(_local.x) >= Math.abs(_local.z)) {
    if (_local.x >= 0) setPlay(p, CLIPS.WALKING_A, true, lokoRate(v, T.ANIM.naturalWalkA, T.ANIM.walkRate), FADE.blockLoko, NO_OUT, 0, -1, -1, 0);
    else setPlay(p, CLIPS.WALKING_BACK, true, lokoRate(v, T.ANIM.naturalWalkBack, T.ANIM.walkRate), FADE.blockLoko, NO_OUT, 0, -1, -1, 0);
  } else {
    setPlay(p, _local.z > 0 ? CLIPS.STRAFE_L : CLIPS.STRAFE_R, true, lokoRate(v, T.ANIM.naturalStrafe, STRAFE_RATE), FADE.blockLoko, NO_OUT, 0, -1, -1, 0);
  }
}

/* ------------------------------------------------------------- Carried soket geometrisi (ANIMATION §6.5) */

function attachToHolder(world: SimWorld, f: Fighter, out: AnimDecision, tick: number, alpha: number): void {
  const holder = f.grabbedBy >= 0 ? world.fighters[f.grabbedBy] : undefined;
  out.attachTo = holder ? holder.id : NO_ID;
  let fwd = T.ANIM.attachFwd, up = T.ANIM.attachUp, side = T.ANIM.attachSide;
  if (holder) {
    if (holder.state === FState.PickingUp) {
      // yerden kaldırma: PickUp [0.70–1.20] boyunca (0.45, −0.75, 0.30) → (0.55, −0.20, 0.30) lerp 22 t (kavrama 16. t'den)
      const k = clamp01((holder.moveTick - T.GRAB.pickUpGripTick + alpha) / T.ANIM.pickUpAttachTicks);
      const from = T.ANIM.pickUpAttachFrom;
      fwd = lerp(from[0], fwd, k); up = lerp(from[1], up, k); side = lerp(from[2], side, k);
    } else if (holder.state === FState.Throwing) {
      // Throw boyunca chest + (0.55, −0.20, 0.30) → throwAttachTo lerp 0–14 t ("kaldırıp fırlatma")
      const k = clamp01((holder.moveTick + alpha) / T.ANIM.throwAttachTicks);
      const to = T.ANIM.throwAttachTo;
      fwd = lerp(fwd, to[0], k); up = lerp(up, to[1], k); side = lerp(side, to[2], k);
    }
  }
  out.attachFwd = fwd; out.attachUp = up; out.attachSide = side; out.attachYawDeg = T.ANIM.attachYawDeg;
  // çırpınma ±8°·sin(2π·3t)
  out.rollDeg = T.ANIM.carriedRollDeg * Math.sin(2 * Math.PI * T.ANIM.carriedRollHz * (tick + alpha) * TICK_DT);
}

/* ------------------------------------------------------------- ana karar */

/**
 * Fighter → AnimDecision. `out` önceki kararı taşır (bellek). alpha: sim alt-tick interpolasyonu (prosedürel açılar için).
 * İmza WP8 fighterView `FighterViewDeps.decide` ile aynı: (world, f, alpha, out).
 */
export function decide(world: SimWorld, f: Fighter, alpha: number, out: AnimDecision): void {
  const tick = world.tick;
  const age = tick - f.stateTick;
  const prevBase = out.base.clip;
  const prevAttach = out.attachTo;
  const prevPinY = out.pinHipsY;   // önceki kare hava klibi miydi (Y-bastır) → iniş kararı (yer Dodge/Escaping aynı Dodge_* klipleri, pin yok)
  const prevSync = out.syncTime;   // varsayılanlar sıfırlamadan önce (hamle startTick kararı, Hold sürekliliği)
  const prevUpper = out.upper.clip, prevUpperStart = out.upper.startTick, prevUpperStartAt = out.upper.startAt;

  // ── varsayılanlar (her kare yeniden yazılır)
  out.syncTime = -1;
  out.paused = f.hitstop > 0 || world.match.globalHitstop > 0;
  out.additiveWeight = 0;
  out.pitchDeg = 0; out.rollDeg = 0; out.tumbleDeg = 0;
  out.squashX = 1; out.squashY = 1;
  out.pinHipsY = false;
  out.attachTo = NO_ID; out.attachFwd = 0; out.attachUp = 0; out.attachSide = 0; out.attachYawDeg = 0;
  out.visible = true; out.opacity = 1; out.shrink = 0;
  closePlay(out.upper);

  // ── additive flinch (ANIMATION §6.1): ana durumu bozmayan darbe (arkadan hafif / süper zırh altında hafif+orta) → Hit_A+ w 0.8 → 0
  //    (0.20 s); zırhta 0.6 / 0.15 s. Darbe BU durumun içinde olmalı (lastHitTick ≥ stateTick): Hitstun'dan çıkınca ikinci flinch binmez.
  //    Zırh: armorUntil ya da hamle zırh penceresi (sim/hits.ts armored() moveTick'e bakar; hitstop'ta moveTick durduğundan darbe anındaki
  //    moveTick alt tahmindir → pencere içi kalır).
  if (f.lastHitTick !== NO_TICK && f.lastHitTick >= f.stateTick &&
      f.state !== FState.Hitstun && f.state !== FState.Launched && f.state !== FState.Tumble && f.state !== FState.Knockdown) {
    const move = f.state === FState.Attack && f.moveIdx >= 0 ? world.moves[f.moveIdx] : undefined;
    const hitMoveTick = f.moveTick - (tick - f.lastHitTick);
    const armored = f.armorUntil > f.lastHitTick ||
      (move !== undefined && move.armor !== undefined && f.moveTick >= move.armor[0] && hitMoveTick <= move.armor[1]);
    if (f.lastHitKind === HitKind.Light || (armored && f.lastHitKind === HitKind.Mid)) {
      const dur = armored ? T.ANIM.flinchArmorSec : T.ANIM.additiveDecay;
      const w0 = armored ? T.ANIM.flinchWeightArmor : T.ANIM.flinchWeight;
      const t = (tick - f.lastHitTick + alpha) * TICK_DT;
      if (t >= 0 && t < dur) out.additiveWeight = w0 * (1 - t / dur);
    }
  }

  // ── maç sonu (sev. 7): Win Cheering loop / Lose Death_A_Pose küçülür (0.4 s)
  const m = world.match;
  if (m.phase === 'over' && f.state !== FState.Eliminated && f.state !== FState.Falling) {
    if (m.winner === f.id) {
      setPlay(out.base, CLIPS.CHEERING, true, 1, FADE.matchEnd, FADE.matchEnd, 0, -1, -1, 0);
    } else {
      setPlay(out.base, CLIPS.DEATH_A_POSE, false, 1, FADE.matchEnd, FADE.matchEnd, 0, -1, -1, m.phaseUntil);
      out.shrink = clamp01((tick - m.phaseUntil + alpha) * TICK_DT / T.FEEL.eliminatedShrinkSec);
      out.visible = out.shrink < 1;
    }
    return;
  }

  // ── parry (durumdan bağımsız pencereler, ANIMATION §2.1): başarı → Melee_Block [0.20–0.33] pause 6 t; dokunuş → BlockEnter 0.20'de paused
  if (f.state === FState.Idle || f.state === FState.Move || f.state === FState.Block) {
    const bonusStart = f.parryBonusUntil - T.BLOCK.parryBonusTicks;
    if (f.parryBonusUntil !== NO_TICK && f.parryBonusUntil > tick && tick - bonusStart < T.BLOCK.parryFreeTicks) {
      setPlay(out.base, CLIPS.BLOCK_PARRY_OK, false, 1, T.ANIM.fadeHitIn, T.ANIM.fadeHitOut, PARRY_OK_START, PARRY_OK_END, PARRY_OK_END, bonusStart);
      out.syncTime = Math.min(PARRY_OK_END, PARRY_OK_START + (tick - bonusStart) * TICK_DT);
      return;
    }
    if (f.parryUntil !== NO_TICK && f.parryUntil > tick && f.state !== FState.Block) {
      // dokunuş: Block'taki BlockEnter action'ı AYNEN sürer (aynı klip + startTick = blockSince → controller reset etmez, crossfade yok),
      // 0.20 karesinde donar; sim blockSince'i bırakmada sıfırlamaz (sim/block.ts). Yedek: pencere başlangıcı.
      const since = f.blockSince !== NO_TICK && f.blockSince <= tick ? f.blockSince : f.parryUntil - T.BLOCK.parryWindowTicks;
      setPlay(out.base, CLIPS.BLOCK_ENTER, false, T.ANIM.blockEnterRate, FADE.parryTapIn, FADE.parryTapOut, 0, PARRY_TAP_PAUSE, PARRY_TAP_PAUSE, since);
      out.syncTime = Math.min(PARRY_TAP_PAUSE, (tick - since) * TICK_DT * T.ANIM.blockEnterRate);
      return;
    }
  }

  switch (f.state) {
    case FState.Idle:
    case FState.Move: {
      // iniş: önceki kare hava klibi (Y-bastır) ise Jump_Land 8 t; başlangıç karar belleğinde tutulur (sim düz inişte hareket kilidi
      // uygulamadığından Idle → Move geçişi stateTick'i sıfırlar; startTick sabit kalır → controller reset etmez). Yer Dodge / Escaping
      // (pinHipsY false) iniş üretmez (ANIMATION §2.1 Dodge_F → Idle 0.15, iniş yok).
      // Deviation (WP2'ye not): düz inişte de JUMP.landTicks hareket kilidi olmalı; şimdilik Jump_Land koşu hızında kayabilir.
      const landStart = prevBase === CLIPS.JUMP_LAND_SHORT ? out.base.startTick : f.stateTick;
      const landing = prevPinY && (AIR_BASES.has(prevBase) || prevBase === CLIPS.JUMP_LAND_SHORT) && f.grounded && tick - landStart < T.JUMP.landTicks;
      if (landing) {
        setPlay(out.base, CLIPS.JUMP_LAND_SHORT, false, T.ANIM.jumpLandRate, FADE.jumpLandIn, FADE.jumpLandOut, 0, -1, -1, landStart);
        out.syncTime = Math.min(JUMP_LAND_SHORT_DUR, (tick - landStart) * TICK_DT * T.ANIM.jumpLandRate);
        out.pinHipsY = true;
        break;
      }
      fullLoko(world, f, out, tick);
      break;
    }
    case FState.Air: {
      out.pinHipsY = true;
      // hızlı iniş kök pitch'i: sim'in yazdığı başlangıç tick'inden tick tabanlı rampa (render hızından bağımsız; alpha ara kare)
      if (f.fastFall && f.fastFallTick !== NO_TICK) {
        out.pitchDeg = T.ANIM.fastFallPitchDeg * clamp01((tick - f.fastFallTick + alpha) / T.ANIM.fastFallPitchTicks);
      }
      // Launched/Tumble sonrası Air (hitstun havada bitti): zıplama yok → Jump_Start / çift zıplama pozu oynatılmaz
      const fromLaunch = prevBase === CLIPS.DEATH_A_LAUNCH || prevBase === CLIPS.DEATH_A_POSE;
      const dblKeep = prevBase === CLIPS.JUMP_START_DOUBLE && tick - out.base.startTick < T.ANIM.jumpStartTicks;
      const dblNew = !fromLaunch && f.doubleJumpLeft < DOUBLE_JUMP_USES && f.vel.y >= T.JUMP.doubleV - DOUBLE_JUMP_VY_MARGIN;
      if (dblKeep || dblNew) {
        const st = dblKeep ? out.base.startTick : tick;
        setPlay(out.base, CLIPS.JUMP_START_DOUBLE, false, T.ANIM.jumpStartRate, FADE.jumpIn, NO_OUT, T.ANIM.jumpStartAt, -1, JUMP_START_DOUBLE_DUR, st);
      } else if (!fromLaunch && age < T.ANIM.jumpStartTicks && f.vel.y > 0) {
        setPlay(out.base, CLIPS.JUMP_START, false, T.ANIM.jumpStartRate, FADE.jumpIn, NO_OUT, T.ANIM.jumpStartAt, -1, -1, f.stateTick);
        out.syncTime = T.ANIM.jumpStartAt + age * TICK_DT * T.ANIM.jumpStartRate;
      } else {
        setPlay(out.base, CLIPS.JUMP_IDLE, true, 1, FADE.jumpIdle, NO_OUT, 0, -1, -1, 0);
      }
      break;
    }
    case FState.Dodge: {
      const clip = dodgeClipOf(world, f);
      const st = f.lastDodgeTick !== NO_TICK ? f.lastDodgeTick : f.stateTick;
      setPlay(out.base, clip, false, T.DODGE.rate, FADE.dodgeIn, NO_OUT, 0, -1, -1, st);
      out.syncTime = Math.min(durOf(clip), (tick - st) * TICK_DT * T.DODGE.rate);
      break;
    }
    case FState.AirDodge: {
      const clip = dodgeClipOf(world, f);
      const st = f.lastDodgeTick !== NO_TICK ? f.lastDodgeTick : f.stateTick;
      setPlay(out.base, clip, false, T.DODGE.rate, FADE.dodgeIn, NO_OUT, 0, -1, -1, st);
      out.syncTime = Math.min(durOf(clip), (tick - st) * TICK_DT * T.DODGE.rate);
      out.pinHipsY = true;
      break;
    }
    case FState.Escaping: {
      // kurtulan kurban: Dodge_Backward tS 1.2 (3 u geri, kök hareket sim'de); Y klipte (yer) → inişte Jump_Land yok
      setPlay(out.base, CLIPS.DODGE_B, false, ESCAPE_DODGE_RATE, FADE.dodgeIn, NO_OUT, 0, -1, -1, f.stateTick);
      out.syncTime = Math.min(durOf(CLIPS.DODGE_B), age * TICK_DT * ESCAPE_DODGE_RATE);
      break;
    }
    case FState.Block: {
      const since = f.blockSince !== NO_TICK ? f.blockSince : f.stateTick;
      const bAge = tick - since;
      if (bAge < T.BLOCK.enterTicks) {
        setPlay(out.base, CLIPS.BLOCK_ENTER, false, T.ANIM.blockEnterRate, FADE.blockEnterIn, NO_OUT, 0, -1, BLOCK_ENTER_END, since);
        out.syncTime = Math.min(BLOCK_ENTER_END, bAge * TICK_DT * T.ANIM.blockEnterRate);
        break;
      }
      const v = lenXZ(f.vel);
      if (v >= T.ANIM.walkMin) blockLoko(f, out.base, v);
      else setPlay(out.base, CLIPS.BLOCKING, true, 1, FADE.blockingIn, NO_OUT, 0, -1, -1, 0);
      break;
    }
    case FState.Hitstun: {
      // Launched yerde bitti, hitstun kalanı yerde sürüyor (sim/knockdown.ts Launched ∧ grounded ∧ tick < stateUntil → Hitstun):
      // Death_A savrulma pozundan Hit_B'ye 0.03 fade yerine Jump_Land (ANIMATION §1 "Launch (havada) → JumpLand", §2.1 JumpLand),
      // kalan hitstun boyunca son karede tutulur. Aynı hitstun'da yeni darbe (lastHitTick ≥ stateTick) normal tepkiye döner. Bu dal
      // parry-sersemliği sezimindeki (Mid ∧ lastHitTick < stateTick ∧ hitstunTicks === 40) iniş yanlış pozitifini de keser.
      const landedFromLaunch = f.grounded && f.lastHitTick < f.stateTick &&
        (prevBase === CLIPS.DEATH_A_LAUNCH || (prevBase === CLIPS.JUMP_LAND_SHORT && out.base.startTick === f.stateTick));
      if (landedFromLaunch) {
        setPlay(out.base, CLIPS.JUMP_LAND_SHORT, false, T.ANIM.jumpLandRate, FADE.jumpLandIn, FADE.jumpLandOut, 0, -1, JUMP_LAND_SHORT_DUR, f.stateTick);
        out.syncTime = Math.min(JUMP_LAND_SHORT_DUR, age * TICK_DT * T.ANIM.jumpLandRate);
        out.pinHipsY = true;
        break;
      }
      const st = f.lastHitTick !== NO_TICK && f.lastHitTick >= f.stateTick ? f.lastHitTick : f.stateTick;
      // hitstop'ta donan tick'ler düşülür (frozenTicks): hitstun süresi de donduğundan klip zamanı çözülmede sıçramaz
      const hAge = Math.max(0, tick - st - frozenTicks(f));
      const stunSec = Math.max(f.hitstunTicks, T.COMBAT.hitstunMinTicks) * TICK_DT;
      // Guard break sezimi Fighter alanlarından: sim kırılma tick'inde guard'ı hemen breakRestart'a çeker ve vuruşu bloksuz uygular
      // (lastHitKind Light/Mid, Blocked DEĞİL; sim/block.ts applyBlockedHit + sim/hits.ts) → guardLastHitTick bu Hitstun'ın tick'i,
      // hitstunTicks ≥ breakTicks, blocking düşmüş. needsWP0: Fighter.guardBrokenTick (ya da HitKind.GuardBreak) ile netleşsin.
      const guardBreak = f.guardLastHitTick === f.stateTick && f.hitstunTicks >= T.BLOCK.breakTicks && !f.blocking;
      // Parry kurbanı (saldıran): sim enterHitstun(…, Mid, parryVictimTicks) verir, lastHitTick'i dokunmaz (kendisi vurulmadı) →
      // Mid ∧ lastHitTick < stateTick ∧ hitstunTicks === parryVictimTicks. needsWP0: HitKind.ParryStun sim ile netleşsin.
      const parryStun = f.lastHitKind === HitKind.Parried ||
        (f.lastHitKind === HitKind.Mid && f.lastHitTick < f.stateTick && f.hitstunTicks === T.BLOCK.parryVictimTicks);
      if (guardBreak) {   // Melee_Block_Hit tam, tS 1.33, 48 t (kalkan düşme pozu)
        setPlay(out.base, CLIPS.BLOCK_HIT, false, T.ANIM.guardBreakRate, T.ANIM.fadeHitIn, FADE.guardBreakOut, 0, -1, BLOCK_HIT_DUR, st);
        out.syncTime = Math.min(BLOCK_HIT_DUR, hAge * TICK_DT * T.ANIM.guardBreakRate);
        break;
      }
      if (parryStun) {    // saldıran sersemliği Hit_B tS 1.3 [0–0.87]
        setPlay(out.base, CLIPS.HIT_B, false, T.ANIM.parryStunRate, T.ANIM.fadeHitIn, FADE.parryStunOut, 0, -1, HIT_B_PARRY_END, st);
        out.syncTime = Math.min(HIT_B_PARRY_END, hAge * TICK_DT * T.ANIM.parryStunRate);
        break;
      }
      switch (f.lastHitKind) {
        case HitKind.Blocked: {   // bloklu isabet Melee_Block_Hit [0–0.5] tS 2.0 (15 t)
          setPlay(out.base, CLIPS.BLOCK_HIT_SHORT, false, T.ANIM.blockHitRate, T.ANIM.fadeHitIn, T.ANIM.fadeHitOut, 0, -1, BLOCK_HIT_SHORT_DUR, st);
          out.syncTime = Math.min(BLOCK_HIT_SHORT_DUR, hAge * TICK_DT * T.ANIM.blockHitRate);
          break;
        }
        case HitKind.Light: {
          const rate = clamp(T.ANIM.hitARef / stunSec, 1, T.ANIM.hitARateMax);
          setPlay(out.base, CLIPS.HIT_A, false, rate, T.ANIM.fadeHitIn, T.ANIM.fadeHitOut, 0, -1, -1, st);
          out.syncTime = Math.min(HIT_A_DUR, hAge * TICK_DT * rate);
          break;
        }
        case HitKind.Lying: {   // yatarken vuruldu: poz korunur (Lying süresi uzamaz)
          setPlay(out.base, f.faceDown ? CLIPS.DEATH_B_POSE : CLIPS.LIE_IDLE, !f.faceDown, 1, FADE.pose, FADE.pose, 0, -1, -1, 0);
          break;
        }
        default: {   // Mid / Finisher / diğer: Hit_B
          const rate = clamp(T.ANIM.hitBRef / stunSec, 1, T.ANIM.hitBRateMax);
          setPlay(out.base, CLIPS.HIT_B, false, rate, T.ANIM.fadeHitIn, T.ANIM.fadeHitOut, 0, -1, -1, st);
          out.syncTime = Math.min(HIT_B_DUR, hAge * TICK_DT * rate);
        }
      }
      break;
    }
    case FState.Launched: {
      const st = f.lastHitTick !== NO_TICK && f.lastHitTick >= f.stateTick ? f.lastHitTick : f.stateTick;
      const lAge = Math.max(0, tick - st - frozenTicks(f));   // donan tick'ler düşülür (frozenTicks)
      setPlay(out.base, CLIPS.DEATH_A_LAUNCH, false, T.ANIM.launchRate, T.ANIM.fadeHitIn, T.ANIM.fadeHitOut, 0, T.ANIM.launchPauseAt, T.ANIM.launchPauseAt, st);
      out.syncTime = Math.min(T.ANIM.launchPauseAt, lAge * TICK_DT * T.ANIM.launchRate);
      // pitch: sim launchPitch (radyan) varsa; yoksa vy'ye göre −15…−50° (vy / v0)
      out.pitchDeg = f.launchPitch !== 0 ? radToDeg(f.launchPitch) : lerp(T.ANIM.launchPitchMin, T.ANIM.launchPitchMax, clamp01(f.vel.y / T.JUMP.v0));
      out.pinHipsY = true;
      break;
    }
    case FState.Tumble: {
      // Y klipte (pin YOK). Deviation — ANIMATION §6.2 "Y-bastır" ile §6.6 pivot geometrisi çelişir: spinPivot (0,1.1,0) + mesh ofseti
      // (0,−0.2,+0.3) bedenin ölçülen merkezini (0,0.2,−0.3) POZ HİPS 0.14 ile hesaplamış; pinlenince (hips 0.406) beden 0.27 u yukarı
      // kayar, dönüş ekseni bedenin altından geçer → eksantrik "çember" (§9 madde 10 "dönüş bedenin ortasından"). Tek karelik statik
      // pozda çift sayım riski (§6.2 gerekçesi) yok; Knockdown'a geçiş de pinsiz Death_A@0.37 (hips 0.46) → süreklilik. Geometri kazandı.
      setPlay(out.base, CLIPS.DEATH_A_POSE, false, 1, FADE.pose, FADE.pose, 0, -1, -1, f.stateTick);
      out.tumbleDeg = radToDeg(f.tumbleAngle);
      out.pinHipsY = false;
      break;
    }
    case FState.Knockdown: {
      // çakılma hitstop'u (7 t) süreye eklenir (sim/knockdown.ts) → donan tick'ler yaştan düşülür, yoksa 11 t'lik çakılmanın 7 t'si atlanırdı
      const kAge = Math.max(0, age - frozenTicks(f));
      if (f.faceDown) {   // Flying Kick whiff: Death_B@1.30-1.75 (13 t)
        setPlay(out.base, CLIPS.DEATH_B_FACEPLANT, false, FACEPLANT_RATE, FADE.pose, FADE.pose, DEATH_B_FP_T0, -1, DEATH_B_FP_T1, f.stateTick);
        out.syncTime = Math.min(DEATH_B_FP_T1, DEATH_B_FP_T0 + kAge * TICK_DT * FACEPLANT_RATE);
      } else {            // yere çakılma Death_A@0.37-0.55 tS 1.0 (11 t), Y klipte
        setPlay(out.base, CLIPS.DEATH_A_KNOCKDOWN, false, T.ANIM.knockdownRate, T.ANIM.fadeHitIn, FADE.pose, DEATH_A_KD_T0, -1, DEATH_A_KD_T1, f.stateTick);
        out.syncTime = Math.min(DEATH_A_KD_T1, DEATH_A_KD_T0 + kAge * TICK_DT * T.ANIM.knockdownRate);
      }
      break;
    }
    case FState.Lying: {
      if (f.faceDown) { setPlay(out.base, CLIPS.DEATH_B_POSE, false, 1, FADE.pose, T.ANIM.fadeLyingIdle, 0, -1, -1, f.stateTick); break; }
      // Carry'den düşürülme: Lie_Down@1.0-1.4 tS 2.0 (0.2 s) → Lying
      const dropped = (prevAttach !== NO_ID || prevBase === CLIPS.LIE_DOWN_DROP) && age < LIE_DOWN_DROP_TICKS;
      if (dropped) {
        setPlay(out.base, CLIPS.LIE_DOWN_DROP, false, LIE_DOWN_DROP_RATE, FADE.pose, T.ANIM.fadeLyingIdle, LIE_DOWN_DROP_T0, -1, LIE_DOWN_DROP_T1, f.stateTick);
        out.syncTime = Math.min(LIE_DOWN_DROP_T1, LIE_DOWN_DROP_T0 + age * TICK_DT * LIE_DOWN_DROP_RATE);
      } else if (age < T.KNOCKDOWN.lyingIdleAfterTicks) {
        setPlay(out.base, CLIPS.DEATH_A_POSE, false, 1, FADE.pose, T.ANIM.fadeLyingIdle, 0, -1, -1, f.stateTick);
      } else {
        setPlay(out.base, CLIPS.LIE_IDLE, true, 1, T.ANIM.fadeLyingIdle, T.ANIM.fadeLyingIdle, 0, -1, -1, 0);
      }
      break;
    }
    case FState.Roll: {
      // Lie_Idle: Lying ile AYNI oynatma (loop, startTick 0, pauseAt yok). Deviation — ANIMATION §6.2/§5 "Lie_Idle t0 pozu paused" der;
      // ama Lying'in Lie_Idle DÖNGÜSÜNDEN t=0'a geçiş görünür bir sıçramadır: aynı klip + yeni startTick'te three aynı action'ı döndürür
      // (prev === next → crossfade yok, three'de bir action kendisiyle crossfade edilemez) ve klip anında 0. kareye atlar; §6.2'nin
      // "0.05 fade"i hiç uygulanamaz. Poz zaten yatan idle, rulo prosedürel → döngünün sürmesi görünmez, sıçrama görünürdü.
      setPlay(out.base, CLIPS.LIE_IDLE, true, 1, FADE.pose, FADE.pose, 0, -1, -1, 0);
      // Prosedürel rulo (modelin ileri ekseni etrafında = visualRoot roll): İŞARET VE TUR SAYISI fighterView.ts ile aynı olmalı —
      // sol bileşen ≥ 0 (sola rulo) → NEGATİF z dönüşü (+Z etrafında + dönüş sol tarafı kaldırır = beden sağa yuvarlanır) ve
      // ROLL_TOTAL_DEG 360° tek tur (§6.2 "180°" ile deviation; gerekçe view'da: Lying sırtüstü Lie_Idle'a dönüş). Tur 3 öncesi burada
      // 180° ve TERS işaret üretiliyordu; view kendi değerini yazdığı için ölü koddu, iki modül zıt sözleşme taşıyordu.
      worldToLocalXZ(_local, f.yaw, f.dodgeDirX, f.dodgeDirZ);
      out.rollDeg = (_local.z >= 0 ? -1 : 1) * ROLL_TOTAL_DEG * clamp01((age + alpha) / T.KNOCKDOWN.rollTicks);
      break;
    }
    case FState.Crawl: {
      const v = lenXZ(f.vel);
      setPlay(out.base, CLIPS.CRAWLING, true, clamp(v / T.ANIM.naturalCrawl, T.ANIM.walkRate[0], T.ANIM.crawlRateMax), FADE.crawl, FADE.crawl, 0, -1, -1, 0);
      break;
    }
    case FState.GetUp: {
      const total = f.stateUntil !== NO_TICK ? f.stateUntil - f.stateTick : T.KNOCKDOWN.getUpTicks;
      const fade = T.ANIM.fadeLyingIdle;   // yüzüstünden / Lie_Idle'dan 0.20 (yuvarlanma okunur)
      if (f.getUpKick || total <= T.KNOCKDOWN.getUpKickTicks) {   // kalkış tekmesi aşama 1: Lie_StandUp@1.60-2.33 tS 2.0 (22 t)
        setPlay(out.base, CLIPS.LIE_STANDUP_QUICK, false, T.ANIM.getUpKickRate, fade, T.ANIM.fadeStage, LIE_STANDUP_QUICK_T0, -1, LIE_STANDUP_QUICK_T1, f.stateTick);
        out.syncTime = Math.min(LIE_STANDUP_QUICK_T1, LIE_STANDUP_QUICK_T0 + age * TICK_DT * T.ANIM.getUpKickRate);
      } else if (total <= T.KNOCKDOWN.quickGetUpTicks) {            // hızlı kalk tS 1.6 (28 t)
        setPlay(out.base, CLIPS.LIE_STANDUP_QUICK, false, T.ANIM.quickGetUpRate, fade, T.ANIM.fadeIdle, LIE_STANDUP_QUICK_T0, -1, LIE_STANDUP_QUICK_T1, f.stateTick);
        out.syncTime = Math.min(LIE_STANDUP_QUICK_T1, LIE_STANDUP_QUICK_T0 + age * TICK_DT * T.ANIM.quickGetUpRate);
      } else {                                                       // GetUp Lie_StandUp@0.70-2.33 tS 2.05 (48 t)
        setPlay(out.base, CLIPS.LIE_STANDUP_GETUP, false, T.ANIM.getUpRate, fade, T.ANIM.fadeIdle, LIE_STANDUP_GETUP_T0, -1, LIE_STANDUP_GETUP_T1, f.stateTick);
        out.syncTime = Math.min(LIE_STANDUP_GETUP_T1, LIE_STANDUP_GETUP_T0 + age * TICK_DT * T.ANIM.getUpRate);
      }
      break;
    }
    case FState.Attack:
    case FState.Throwing:
    case FState.GrabAttempt:
    case FState.PickingUp: {
      const move = f.moveIdx >= 0 ? world.moves[f.moveIdx] : undefined;
      if (move) { applyMove(world, f, move, out, tick, prevSync, prevUpper, prevUpperStart, prevUpperStartAt); break; }
      // MoveDef'siz yedekler (sim moveIdx vermediyse; ANIMATION §2.2 sayıları)
      if (f.state === FState.Throwing) {
        setPlay(out.base, CLIPS.THROW, false, FALLBACK_THROW_RATE, T.ANIM.fadeAttackIn, T.ANIM.fadeAttackOut, FALLBACK_THROW_START, -1, -1, f.stateTick);
        out.syncTime = Math.min(THROW_DUR, FALLBACK_THROW_START + age * TICK_DT * FALLBACK_THROW_RATE);
      } else if (f.state === FState.GrabAttempt) {
        setPlay(out.base, CLIPS.INTERACT, false, FALLBACK_GRAB_RATE, T.ANIM.fadeAttackIn, T.ANIM.fadeAttackOut, FALLBACK_GRAB_START, -1, -1, f.stateTick);
        out.syncTime = Math.min(INTERACT_DUR, FALLBACK_GRAB_START + age * TICK_DT * FALLBACK_GRAB_RATE);
      } else if (f.state === FState.PickingUp && f.grabTarget !== NO_ID) {   // G3: PickUp@0.15-0.70 (0–15 t) → @0.70-1.20 (16. t'den, 0.06 fade)
        if (age < T.GRAB.pickUpGripTick) {
          setPlay(out.base, CLIPS.PICKUP_GRIP, false, T.ANIM.pickUpRate, T.ANIM.fadeAttackIn, T.ANIM.fadeStageG3, PICKUP_GRIP_T0, -1, PICKUP_GRIP_T1, f.stateTick);
          out.syncTime = Math.min(PICKUP_GRIP_T1, PICKUP_GRIP_T0 + age * TICK_DT * T.ANIM.pickUpRate);
        } else {
          setPlay(out.base, CLIPS.PICKUP_RAISE, false, T.ANIM.pickUpRate, T.ANIM.fadeStageG3, T.ANIM.fadeAttackOut, PICKUP_RAISE_T0, -1, PICKUP_RAISE_T1, f.stateTick + T.GRAB.pickUpGripTick);
          out.syncTime = Math.min(PICKUP_RAISE_T1, PICKUP_RAISE_T0 + (age - T.GRAB.pickUpGripTick) * TICK_DT * T.ANIM.pickUpRate);
        }
      } else if (f.state === FState.PickingUp) {   // eşya alma PickUp@0-0.65 tS 2.2 (18 t; equip 14. t)
        setPlay(out.base, CLIPS.PICKUP_ITEM, false, T.ANIM.pickUpItemRate, T.ANIM.fadeAttackIn, T.ANIM.fadeAttackOut, 0, -1, PICKUP_ITEM_T1, f.stateTick);
        out.syncTime = Math.min(PICKUP_ITEM_T1, age * TICK_DT * T.ANIM.pickUpItemRate);
      } else {
        fullLoko(world, f, out, tick);
      }
      break;
    }
    case FState.Hold: {
      // Interact "son kare paused" (ANIMATION §2.2 G1b): GrabAttempt'in Interact action'ı SÜRER (aynı klip + startTick + startAt →
      // controller reset etmez, geri sarmaz); donma karesi 0.57 ya da tutma anında klip zaten ilerideyse o an (pauseAt bellekte kalır).
      // Idle'dan doğrudan Hold'a (test / sahne): 0.57'de başlar ve durur.
      const cont = prevBase === CLIPS.INTERACT;
      const startTick = cont ? out.base.startTick : f.stateTick;
      const startAt = cont ? out.base.startAt : T.ANIM.holdPauseAt;
      const pauseAt = cont ? (out.base.pauseAt >= 0 ? out.base.pauseAt : Math.max(T.ANIM.holdPauseAt, prevSync)) : T.ANIM.holdPauseAt;
      setPlay(out.base, CLIPS.INTERACT, false, HOLD_RATE, T.ANIM.fadeStage, T.ANIM.fadeAttackOut, startAt, pauseAt, -1, startTick);
      break;
    }
    case FState.Lifting: {   // omuza alma Interact@0.65-1.30 tS 1.5 (26 t)
      setPlay(out.base, CLIPS.INTERACT_LIFT, false, T.ANIM.liftRate, T.ANIM.fadeStage, T.ANIM.fadeUpperCarry, INTERACT_LIFT_T0, -1, INTERACT_LIFT_T1, f.stateTick);
      out.syncTime = Math.min(INTERACT_LIFT_T1, INTERACT_LIFT_T0 + age * TICK_DT * T.ANIM.liftRate);
      break;
    }
    case FState.Held: {
      const holder = f.grabbedBy >= 0 ? world.fighters[f.grabbedBy] : undefined;
      const lifting = holder !== undefined && holder.state === FState.Lifting && tick - holder.stateTick >= T.GRAB.liftAttachTick;
      const throwing = holder !== undefined && holder.state === FState.Throwing;
      if (lifting || throwing) {   // 13. t'de Death_A_Pose (0.15 fade) + chest soketi; Throw boyunca sokette kalır
        setPlay(out.base, CLIPS.DEATH_A_POSE, false, 1, FADE.heldPoseIn, FADE.pose, 0, -1, -1, holder.stateTick);
        attachToHolder(world, f, out, tick, alpha);
      } else {
        // Hit_A 0.20 karesinde paused ("yakalandı"), tutana bakar (yaw sim'de). Hitstun'dan geliyorsa (Hit_A zaten oynuyor) Hold/Interact
        // kalıbı: AYNI action sürer (startTick/startAt korunur → controller reset etmez) ve klip GERİ SARMAZ — pauseAt = max(0.20, o anki
        // klip zamanı. Aksi hâlde t ≈ 0.4–0.6'daki Hit_A 0.20'ye zıplardı (aynı action kendisiyle crossfade edilemez → görünür pop).
        const cont = prevBase === CLIPS.HIT_A;
        const startTick = cont ? out.base.startTick : f.stateTick;
        const startAt = cont ? out.base.startAt : T.ANIM.heldPauseAt;
        const pauseAt = cont ? (out.base.pauseAt >= 0 ? out.base.pauseAt : Math.max(T.ANIM.heldPauseAt, prevSync)) : T.ANIM.heldPauseAt;
        setPlay(out.base, CLIPS.HIT_A, false, 1, FADE.heldHitIn, T.ANIM.fadeHitOut, startAt, pauseAt, -1, startTick);
      }
      break;
    }
    case FState.Carried: {
      setPlay(out.base, CLIPS.DEATH_A_POSE, false, 1, FADE.carriedIn, FADE.pose, 0, -1, -1, f.stateTick);
      attachToHolder(world, f, out, tick, alpha);
      break;
    }
    case FState.Carrying: {
      // üst Holding_A#upper (TL gelmeden controller Melee_Blocking#upper'a düşer) + alt Idle_A / Walking_A / Running_A #lower
      setPlay(out.upper, CLIPS.HOLDING_A_UPPER, true, 1, T.ANIM.fadeUpperCarry, FADE.carryUpperOut, 0, -1, -1, 0);
      lowerLoko(f, out.base, f.grounded);
      out.pinHipsY = !f.grounded;
      break;
    }
    case FState.Taunt: {
      const def = CHARACTERS[f.charIdx];
      const clip = def ? def.taunt : CLIPS.CHEERING;
      const rate = clip === CLIPS.CHEERING ? T.ANIM.tauntRate : 1;
      const end = durOf(clip);
      setPlay(out.base, clip, false, rate, FADE.taunt, FADE.taunt, 0, -1, end, f.stateTick);
      out.syncTime = Math.min(end, age * TICK_DT * rate);
      break;
    }
    case FState.Spawning:
    case FState.Respawning: {   // Spawn_Air tS 1.3 (1.0 s); hips Y klipte (2.0 → 0.28), fizik kapsülü yerde
      setPlay(out.base, CLIPS.SPAWN_AIR, false, T.ANIM.spawnRate, FADE.spawnIn, T.ANIM.fadeIdle, 0, -1, SPAWN_AIR_DUR, f.stateTick);
      out.syncTime = Math.min(SPAWN_AIR_DUR, age * TICK_DT * T.ANIM.spawnRate);
      break;
    }
    case FState.Falling: {   // ring-out: Jump_Idle tS 0.6 + 540°/s dönüş, 0.25 s'de ölçek 1→0; tumble'daysa Death_A_Pose dönerek
      const since = f.fellAt !== NO_TICK ? f.fellAt : f.stateTick;
      const t = (tick - since + alpha) * TICK_DT;
      const wasTumble = prevBase === CLIPS.DEATH_A_POSE || prevBase === CLIPS.DEATH_A_LAUNCH;
      if (wasTumble) { setPlay(out.base, CLIPS.DEATH_A_POSE, false, 1, FADE.pose, FADE.pose, 0, -1, -1, since); out.tumbleDeg = radToDeg(f.tumbleAngle); }
      else setPlay(out.base, CLIPS.JUMP_IDLE, true, T.FEEL.fellIdleRate, FADE.pose, FADE.pose, 0, -1, -1, 0);
      // Deviation: DESIGN §8 / ANIMATION §2.1 Y (yaw) dönüşü ister; AnimDecision'da yaw alanı yok → roll (needsWP0: AnimDecision.yawDeg,
      // fighterView rotation.set(pitch, yaw, roll); sonra burada out.yawDeg = (fellSpinDegPerSec·t) % 360, rollDeg 0).
      out.rollDeg = (T.FEEL.fellSpinDegPerSec * t) % 360;
      out.shrink = clamp01(t / T.FEEL.fellShrinkSec);
      out.visible = out.shrink < 1;
      out.pinHipsY = !wasTumble;   // tumble'da Death_A_Pose pinsiz (Tumble dalıyla aynı gerekçe: pivot geometrisi hips 0.14'e göre)
      break;
    }
    case FState.Eliminated: {
      setPlay(out.base, CLIPS.DEATH_A_POSE, false, 1, FADE.matchEnd, FADE.matchEnd, 0, -1, -1, f.stateTick);
      out.shrink = clamp01((age + alpha) * TICK_DT / T.FEEL.eliminatedShrinkSec);
      out.visible = out.shrink < 1;
      break;
    }
    default:
      fullLoko(world, f, out, tick);
  }
}
